"use client";

import { usePathname } from "next/navigation";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  getCachedWebUser,
  pathNeedsImmediateAuthBoot,
} from "@/lib/firebase/session-cache";
import type { WebAuthUser } from "@/lib/firebase/types";

type FirebaseAuthContextValue = {
  user: WebAuthUser | null;
  loading: boolean;
  authReady: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signInWithPairToken: (customToken: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  wakeAuth: () => void;
};

const FirebaseAuthContext = createContext<FirebaseAuthContextValue | null>(
  null,
);

const INTERACTION_BOOT_EVENTS = ["pointerdown", "keydown", "touchstart"] as const;
const AUTH_RUNTIME_URL = "/scripts/firebase-auth-runtime.js";
const loadAuthRuntime = () =>
  import(
    /* webpackIgnore: true */
    AUTH_RUNTIME_URL
  ) as Promise<typeof import("@/lib/firebase/auth-runtime")>;

function readInitialUser(): WebAuthUser | null {
  if (typeof window === "undefined") {
    return null;
  }
  return getCachedWebUser();
}

export function FirebaseAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const [user, setUser] = useState<WebAuthUser | null>(readInitialUser);
  const [loading, setLoading] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const bootStartedRef = useRef(false);

  const attachAuthListener = useCallback(async () => {
    if (unsubscribeRef.current) {
      return;
    }

    const runtime = await loadAuthRuntime();
    const { getFirebaseAuth } = runtime;
    const authInstance = getFirebaseAuth();
    await authInstance.authStateReady();

    const firebaseUser = authInstance.currentUser;
    if (firebaseUser && !firebaseUser.isAnonymous) {
      setUser((prev) => prev ?? runtime.webAuthUserFromFirebase(firebaseUser));
    } else if (!getCachedWebUser()) {
      setUser(null);
    }

    unsubscribeRef.current = runtime.authActions.subscribeAuth((next) => {
      setUser(next);
      setAuthReady(true);
      setLoading(false);
      if (next) {
        void import("@/lib/turkuSongLookup")
          .then(({ loadSongLookup }) => loadSongLookup())
          .catch(() => undefined);
      }
    });

    setAuthReady(true);
    setLoading(false);
  }, []);

  const startAuthBoot = useCallback(() => {
    if (bootStartedRef.current) {
      return;
    }
    bootStartedRef.current = true;
    void attachAuthListener();
  }, [attachAuthListener]);

  const wakeAuth = useCallback(() => {
    startAuthBoot();
  }, [startAuthBoot]);

  useEffect(() => {
    const cachedUser = getCachedWebUser();
    const needsImmediate =
      Boolean(cachedUser) || pathNeedsImmediateAuthBoot(pathname ?? "");

    if (needsImmediate) {
      startAuthBoot();
      return;
    }

    const bootOnInteraction = () => {
      startAuthBoot();
      cleanup();
    };

    const cleanup = () => {
      for (const event of INTERACTION_BOOT_EVENTS) {
        window.removeEventListener(event, bootOnInteraction, true);
      }
    };

    for (const event of INTERACTION_BOOT_EVENTS) {
      window.addEventListener(event, bootOnInteraction, {
        capture: true,
        once: true,
        passive: true,
      });
    }

    return cleanup;
  }, [pathname, startAuthBoot]);

  useEffect(() => {
    return () => {
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
      bootStartedRef.current = false;
    };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setLoading(true);
    try {
      const { authActions } = await loadAuthRuntime();
      startAuthBoot();
      await attachAuthListener();
      await authActions.signInWithGoogle();
    } finally {
      setLoading(false);
    }
  }, [attachAuthListener, startAuthBoot]);

  const signInWithApple = useCallback(async () => {
    setLoading(true);
    try {
      const { authActions } = await loadAuthRuntime();
      startAuthBoot();
      await attachAuthListener();
      await authActions.signInWithApple();
    } finally {
      setLoading(false);
    }
  }, [attachAuthListener, startAuthBoot]);

  const signInWithPairToken = useCallback(
    async (customToken: string) => {
      const { authActions } = await loadAuthRuntime();
      startAuthBoot();
      await attachAuthListener();
      const signedIn = await authActions.signInWithPairCustomToken(customToken);
      setUser(signedIn);
      setAuthReady(true);
    },
    [attachAuthListener, startAuthBoot],
  );

  const signOut = useCallback(async () => {
    setLoading(true);
    try {
      const { authActions } = await loadAuthRuntime();
      await authActions.signOut();
      setUser(null);
    } finally {
      setAuthReady(true);
      setLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    startAuthBoot();
    await attachAuthListener();
    const runtime = await loadAuthRuntime();
    const firebaseUser = runtime.getFirebaseAuth().currentUser;
    if (!firebaseUser || firebaseUser.isAnonymous) {
      setUser(null);
      return;
    }
    const next = await runtime.loadWebAuthUser(firebaseUser);
    setUser(next);
  }, [attachAuthListener, startAuthBoot]);

  const value = useMemo(
    () => ({
      user,
      loading,
      authReady,
      signInWithGoogle,
      signInWithApple,
      signInWithPairToken,
      signOut,
      refreshUser,
      wakeAuth,
    }),
    [
      user,
      loading,
      authReady,
      signInWithGoogle,
      signInWithApple,
      signInWithPairToken,
      signOut,
      refreshUser,
      wakeAuth,
    ],
  );

  return (
    <FirebaseAuthContext.Provider value={value}>
      {children}
    </FirebaseAuthContext.Provider>
  );
}

export function useFirebaseAuth(): FirebaseAuthContextValue {
  const ctx = useContext(FirebaseAuthContext);
  if (!ctx) {
    throw new Error("useFirebaseAuth must be used within FirebaseAuthProvider");
  }
  return ctx;
}
